import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "titleInput", "descriptionInput", "targetDateInput",
    "kindSelect", "sequentialInput",
    "criticalSelect", "priorityIcon",
    "approversPicker", "distributionPicker", "externalUsersPicker", "bicPicker",
    "specDrawingsInput", "taskSelect",
    "submitBtn", "approversError",
    "dropzone", "fileInput", "fileList", "photoList",
    "uploadStatus", "uploadStatusText",
    "saveDraftBtn",
    "docPicker", "docFolders", "docFiles", "docCount", "docAddBtn",
    "remindBeforeSelect", "remindAfterSelect",
    // Checklist inside the item (K3-38)
    "checklistSection", "checklistSwitchRow", "checklistSwitch", "checklistKnob", "checklistState",
    "checklistBox", "checklistList", "checklistNewInput", "checklistDictateBtn", "checklistDictateLabel",
    "checklistPhotoInput", "checklistFileInput", "checklistTemplateSelect", "checklistAiBtn",
    "checklistPasteBox", "checklistPasteInput", "checklistTemplateNameBox", "checklistTemplateNameInput",
    "checklistAutoSwitch", "checklistAutoKnob", "checklistCount", "layout", "formColumn", "checklistColumn",
    // Submittal package + schedule (K3-40)
    "submittalBox", "packageSelect", "packageNewInput", "neededOnSiteInput", "leadTimeInput", "reviewDaysInput", "submitByText"
  ]

  static values = {
    projectId: Number,
    nextSequentialUrl: String,
    checkSequentialUrl: String,
    kindUrl: String,
    members: Array,
    submitUrl: String,
    method: { type: String, default: "POST" },
    isEdit: { type: Boolean, default: false },
    // Editing a saved draft (Larry 2026-09-08): Create publishes it.
    draft: { type: Boolean, default: false },
    // Company setting "Make Assigned To optional" (Phelipe 2026-09-01).
    assignedToOptional: { type: Boolean, default: false },
    presignUrl: String,
    registerUrl: String,
    pollThumbnailsUrl: String,
    existingPhotos: { type: Array, default: [] },
    existingFiles: { type: Array, default: [] },
    documentsTreeUrl: String,
    documentsFilesUrl: String,
    attachDocumentsUrl: String
  }

  connect() {
    this._approverEmails = []
    this._bicTouched = false
    this._distributionEmails = []
    this._externalUserEmails = []

    // Edit mode: remember the persisted kind + number so toggling the Type
    // select away and back doesn't hand the item a brand-new number.
    this._originalKindId = this.hasKindSelectTarget ? (this.kindSelectTarget.dataset.selectedId || null) : null
    this._originalSequential = this.hasSequentialInputTarget ? this.sequentialInputTarget.value : ""
    this._seqFetchId = 0

    // Upload state
    this._uploadPhotos = []
    this._uploadFiles = []
    this._photoIds = []
    this._fileIds = []
    this._removedIds = []
    this._photoComments = {}
    this._fileNames = {}
    this._pendingUploads = 0
    this._eagerUploadPromises = []

    // Drafts are explicit now (Larry 2026-09-08): "Save draft" keeps the item
    // on the server; nothing is kept in the browser when you leave the page.
    this._loadKinds()
    this._updatePriorityIcon()
    this._loadExistingAttachments()
    this._initChecklist()
    this._toggleSubmittalBox()

    // Paste a screenshot or copied file straight onto the form (Phelipe
    // 2026-09-01) — same convenience as ChatLog and the item's chat. Bound
    // on the document so it works wherever the cursor is on the form, but
    // it never steals a paste meant for a text field.
    this._onFormPaste = e => this.pasteFiles(e)
    document.addEventListener("paste", this._onFormPaste)
  }

  disconnect() {
    if (this._checklistRecognition) this._stopChecklistDictation()
    if (this._onChecklistResize) window.removeEventListener("resize", this._onChecklistResize)
    if (this._onFormPaste) document.removeEventListener("paste", this._onFormPaste)
  }

  _pickerEmails(target, identifier = "member-multi-select") {
    const ctrl = target ? this.application.getControllerForElementAndIdentifier(target, identifier) : null
    return ctrl ? ctrl.selectedEmails : []
  }


  pasteFiles(e) {
    const files = [...(e.clipboardData?.files || [])]
    if (files.length === 0) return

    const target = e.target
    const typingElsewhere = target instanceof HTMLElement &&
      target.closest("input:not([type=file]), textarea, [contenteditable='true']")
    if (typingElsewhere && !this.element.contains(target)) return

    e.preventDefault()
    if (this.hasDropzoneTarget) {
      // Flash the zone so it is obvious where the paste landed.
      this.dropzoneTarget.classList.add("border-brand", "bg-brand/5")
      setTimeout(() => this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5"), 600)
    }

    this._sortAndUpload(files.map((file, i) => (
      file.name ? file : new File([file], `pasted-${Date.now()}${files.length > 1 ? `-${i + 1}` : ""}.png`, { type: file.type || "image/png" })
    )))
  }

  // ── Kind ───────────────────────────────────────────────────────────

  async _loadKinds() {
    if (!this.kindUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      let url = this.kindUrlValue
      const select = this.kindSelectTarget
      if (this.isEditValue && select.dataset.selectedId) {
        const sep = url.includes("?") ? "&" : "?"
        url += `${sep}current_kind_id=${select.dataset.selectedId}`
      }
      const res = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await res.json()
      const kinds = data.kinds || data
      kinds.forEach(k => {
        const opt = document.createElement("option")
        opt.value = k.id
        opt.textContent = k.hidden ? `${k.name} (Deactivated)` : k.name
        select.appendChild(opt)
      })

      if (this.isEditValue && select.dataset.selectedId) {
        select.value = select.dataset.selectedId
      } else if (kinds.length > 0) {
        const defaultKind = kinds.find(k => k.default) || kinds[0]
        select.value = defaultKind.id
        this._fetchSequential(defaultKind.id)
      }
    } catch (e) {
      console.error("[ActionItemForm] loadKinds error:", e)
    }
  }

  kindChanged() {
    const kindId = this.kindSelectTarget.value
    this._toggleSubmittalBox()
    if (!kindId) {
      this._seqFetchId++ // invalidate any in-flight fetch
      this.sequentialInputTarget.value = ""
      return
    }
    // Back on the item's own kind -> its own number, not "next available"
    if (this.isEditValue && this._originalKindId && kindId === this._originalKindId) {
      this._seqFetchId++
      this.sequentialInputTarget.value = this._originalSequential
      return
    }
    this._fetchSequential(kindId)
  }

  async _fetchSequential(kindId) {
    const fetchId = ++this._seqFetchId
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const url = `${this.nextSequentialUrlValue}?kind_id=${kindId}`
      const res = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await res.json()
      if (fetchId !== this._seqFetchId) return // superseded by a newer selection
      if (data && data.sequential != null) this.sequentialInputTarget.value = data.sequential
    } catch (e) {
      console.error("[ActionItemForm] fetchSequential error:", e)
    }
  }

  // ── Priority ─────────────────────────────────────────────────────────

  priorityChanged() {
    this._updatePriorityIcon()
  }

  _updatePriorityIcon() {
    if (!this.hasPriorityIconTarget || !this.hasCriticalSelectTarget) return
    const level = this.criticalSelectTarget.value
    const triangle = '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>'
    if (level === "high") {
      this.priorityIconTarget.innerHTML = triangle
      this.priorityIconTarget.className = "absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-red-500"
      this.criticalSelectTarget.classList.add("pl-8")
      this.criticalSelectTarget.classList.remove("pl-3")
    } else if (level === "medium") {
      this.priorityIconTarget.innerHTML = triangle
      this.priorityIconTarget.className = "absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-amber-500"
      this.criticalSelectTarget.classList.add("pl-8")
      this.criticalSelectTarget.classList.remove("pl-3")
    } else {
      this.priorityIconTarget.innerHTML = ""
      this.criticalSelectTarget.classList.add("pl-3")
      this.criticalSelectTarget.classList.remove("pl-8")
    }
  }

  // ── Member multi-select events ─────────────────────────────────────

  approversChanged(event) {
    this._approverEmails = event.detail?.emails || []
    if (this._approverEmails.length > 0 && this.hasApproversErrorTarget) {
      this.approversErrorTarget.classList.add("hidden")
    }
    this._syncBallInCourtToAssignee()
  }

  // Ball in Court follows Assigned To until the user picks someone else
  // there themselves (Larry 2026-08-04).
  bicChanged() {
    this._bicTouched = true
  }

  _syncBallInCourtToAssignee() {
    if (this._bicTouched || !this.hasBicPickerTarget) return
    const ctrl = this.application.getControllerForElementAndIdentifier(
      this.bicPickerTarget, "member-multi-select"
    )
    if (!ctrl || typeof ctrl.setSelection !== "function") return
    ctrl.setSelection(this._approverEmails.slice(0, 1))
  }

  distributionChanged(event) {
    this._distributionEmails = event.detail?.emails || []
  }

  externalUsersChanged(event) {
    this._externalUserEmails = event.detail?.emails || []
  }

  // ── File upload: drag/drop + browse ────────────────────────────────

  triggerFileInput() {
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  handleFiles() {
    if (!this.hasFileInputTarget || !this.fileInputTarget.files.length) return
    this._sortAndUpload(Array.from(this.fileInputTarget.files))
    this.fileInputTarget.value = ""
  }

  dragOver(e) {
    e.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("border-brand", "bg-brand/5")
    }
  }

  dragLeave(e) {
    e.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5")
    }
  }

  dropFiles(e) {
    e.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5")
    }
    if (e.dataTransfer.files.length) {
      this._sortAndUpload(Array.from(e.dataTransfer.files))
    }
  }

  _sortAndUpload(files) {
    const imageFiles = []
    const otherFiles = []

    files.forEach(file => {
      if (this._isImageFile(file)) {
        imageFiles.push(file)
      } else {
        otherFiles.push(file)
      }
    })

    if (imageFiles.length) this._addPhotoFiles(imageFiles)
    if (otherFiles.length) this._addFiles(otherFiles)
  }

  _isImageFile(file) {
    if (file.type.startsWith("image/")) return true
    if (this._isHeic(file)) return true
    const ext = file.name.split(".").pop().toLowerCase()
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "dng"].includes(ext)
  }

  // ── Photo upload ────────────────────────────────────────────────────

  _addPhotoFiles(fileList) {
    const newEntries = []
    fileList.forEach(file => {
      const entry = { file, comment: "", convertedBlob: null, _uploadResult: null }
      this._uploadPhotos.push(entry)
      const index = this._uploadPhotos.length - 1
      this._renderPhotoUploadRow(index, file)
      newEntries.push({ entry, index })

      if (this._isHeic(file)) {
        entry._heicPromise = this._convertHeic(file, index, entry)
      }
    })

    this._eagerPhotoUpload(newEntries)
  }

  _renderPhotoUploadRow(index, file, opts = {}) {
    if (!this.hasPhotoListTarget) return

    const row = document.createElement("div")
    row.className = "flex items-start gap-2 p-2 rounded-lg photo-row-bg"
    row.dataset.uploadPhotoIndex = index

    // Thumbnail
    const thumbDiv = document.createElement("div")
    thumbDiv.className = "w-14 h-14 rounded shrink-0 overflow-hidden flex items-center justify-center photo-thumb-bg"
    thumbDiv.dataset.role = "upload-thumb"

    if (opts.thumbnailUrl) {
      const img = document.createElement("img")
      img.className = "w-full h-full object-cover"
      img.src = opts.thumbnailUrl
      img.onerror = () => { thumbDiv.innerHTML = this._imagePlaceholderSvg() }
      thumbDiv.appendChild(img)
    } else if (this._isHeic(file)) {
      thumbDiv.innerHTML = this._spinnerSvg()
    } else if (file && file.type && file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.className = "w-full h-full object-cover"
      const blobUrl = URL.createObjectURL(file)
      img.src = blobUrl
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl)
        img.remove()
        thumbDiv.innerHTML = this._imagePlaceholderSvg()
      }
      thumbDiv.appendChild(img)
    } else {
      thumbDiv.innerHTML = this._imagePlaceholderSvg()
    }
    row.appendChild(thumbDiv)

    // Info + comment
    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    const nameP = document.createElement("p")
    nameP.className = "font-medium text-gray-700 truncate"
    nameP.style.fontSize = "13px"
    nameP.textContent = opts.filename || (file && file.name) || "Photo"
    info.appendChild(nameP)

    const commentInput = document.createElement("input")
    commentInput.type = "text"
    commentInput.placeholder = "Add comment..."
    commentInput.className = "mt-1 w-full px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-brand focus:border-brand"
    commentInput.style.fontSize = "13px"
    commentInput.value = opts.comment || ""
    commentInput.dataset.uploadPhotoIndex = index
    commentInput.addEventListener("input", () => {
      if (this._uploadPhotos[index]) {
        this._uploadPhotos[index].comment = commentInput.value
        const photoId = this._uploadPhotos[index]?._uploadResult?.photo_id
        if (photoId) {
          this._photoComments[photoId] = commentInput.value
        }
      }
    })
    info.appendChild(commentInput)
    row.appendChild(info)

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "p-1 text-gray-400 hover:text-gray-600 shrink-0"
    removeBtn.innerHTML = this._xSvg()
    removeBtn.addEventListener("click", () => {
      const entry = this._uploadPhotos[index]
      if (entry?._uploadResult?.photo_id) {
        const pid = entry._uploadResult.photo_id
        this._photoIds = this._photoIds.filter(id => id !== pid)
        delete this._photoComments[pid]
        if (entry._uploadResult._existing) this._removedIds.push(pid)
      }
      this._uploadPhotos[index] = null
      row.remove()
    })
    row.appendChild(removeBtn)

    this.photoListTarget.appendChild(row)
  }

  async _eagerPhotoUpload(entries) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this._startUpload()

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: entries.map(({ entry }) => ({
            filename: entry.file.name,
            content_type: entry.file.type || "application/octet-stream",
            byte_size: entry.file.size
          }))
        })
      })

      if (!presignRes.ok) {
        console.error("Presign failed")
        return
      }

      const { uploads } = await presignRes.json()

      const uploadPromises = entries.map(async ({ entry, index }, i) => {
        const upload = uploads[i]
        const prepared = await this._prepareFile(entry)

        const res = await fetch(upload.upload_url, {
          method: "PUT",
          headers: { "Content-Type": prepared.blob.type || upload.content_type },
          body: prepared.blob
        })
        if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)

        entry._uploadResult = {
          cache_name: upload.cache_name,
          filename: upload.filename,
          _blobUrl: URL.createObjectURL(prepared.blob)
        }
      })

      const batchPromise = Promise.allSettled(uploadPromises)
      this._eagerUploadPromises.push(batchPromise)
      await batchPromise

      // Register successful uploads
      const successful = entries.filter(({ entry }) => entry?._uploadResult)
      if (successful.length === 0) return

      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(({ entry }) => ({
            cache_name: entry._uploadResult.cache_name,
            filename: entry._uploadResult.filename
          }))
        })
      })

      const registered = await registerRes.json()

      registered.forEach((a, i) => {
        const { entry } = successful[i]
        entry._uploadResult.photo_id = a.photo_id
        this._photoIds.push(a.photo_id)
        if (entry.comment) this._photoComments[a.photo_id] = entry.comment
      })
      if (this._queueDraftSave) this._queueDraftSave()

      // Poll for thumbnails
      const photoIds = registered.map(a => a.photo_id)
      if (this.pollThumbnailsUrlValue) {
        this._pollForThumbnails(photoIds)
      }
    } catch (e) {
      console.error("Photo upload failed:", e)
    } finally {
      this._finishUpload()
    }
  }

  // ── File upload (non-image) ─────────────────────────────────────────

  _addFiles(fileList) {
    const entries = []
    fileList.forEach(file => {
      const entry = { file, _uploadResult: null }
      this._uploadFiles.push(entry)
      const index = this._uploadFiles.length - 1
      entries.push({ entry, index })
    })
    this._doFileUpload(entries)
  }

  async _doFileUpload(entries) {
    if (!this.hasFileListTarget) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this._startUpload()

    // Show uploading placeholders
    const placeholders = entries.map(({ entry }) => {
      const chip = this._createFileChip(entry.file.name, { uploading: true })
      this.fileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: entries.map(({ entry }) => ({
            filename: entry.file.name,
            content_type: entry.file.type || "application/octet-stream",
            byte_size: entry.file.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const file = entries[i].entry.file
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || upload.content_type },
            body: file
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      // Replace placeholders with final chips
      placeholders.forEach(p => p.remove())
      registered.forEach(a => {
        const name = a.filename || `File #${a.id}`
        this._fileIds.push(a.photo_id)
        this._fileNames[a.photo_id] = name
        if (this._queueDraftSave) this._queueDraftSave()

        const chip = this._createFileChip(name, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._fileIds = this._fileIds.filter(id => id !== a.photo_id)
          }
        })
        this.fileListTarget.appendChild(chip)
      })
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
    } finally {
      this._finishUpload()
    }
  }

  // ── Pick from Documents (Kloepfer K3-2, 2026-09-24) ──────────────────
  // The project's Documents folders this person can see; the files ticked
  // are copied onto the item like any upload (the server does the copying).
  async openDocPicker() {
    if (!this.hasDocPickerTarget) return
    this._docPicked = this._docPicked || new Map()
    this.docPickerTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._paintDocCount()
    if (this._docTree) return
    this.docFoldersTarget.innerHTML = `<p class="px-4 py-2 text-[13px] text-gray-500">Loading folders…</p>`
    this.docFilesTarget.innerHTML = ""
    try {
      const res = await fetch(this.documentsTreeUrlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!res.ok) throw new Error("tree")
      this._docTree = (await res.json()).folders || []
      this._renderDocFolders()
    } catch (_) {
      this.docFoldersTarget.innerHTML = `<p class="px-4 py-2 text-[13px] text-red-600">Couldn't load the folders — close this and try again.</p>`
    }
  }

  closeDocPicker(e) {
    if (e) e.preventDefault()
    if (!this.hasDocPickerTarget) return
    this.docPickerTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  stopPropagation(e) { e.stopPropagation() }

  _renderDocFolders() {
    const rows = []
    const walk = (nodes, depth) => (nodes || []).forEach(n => {
      rows.push(`<button type="button" data-folder-id="${n.id}" style="padding-left:${12 + depth * 14}px"
        class="w-full flex items-center gap-2 pr-3 py-1.5 text-left text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer">
        <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"/></svg>
        <span class="truncate flex-1">${this._escHtml(n.name)}</span><span class="text-gray-400 text-[12px]">${n.files_count || 0}</span></button>`)
      walk(n.children, depth + 1)
    })
    walk(this._docTree, 0)
    this.docFoldersTarget.innerHTML = rows.length ? rows.join("")
      : `<p class="px-4 py-2 text-[13px] text-gray-500">No folders you can see in this project's Documents.</p>`
    this.docFoldersTarget.querySelectorAll("[data-folder-id]").forEach(b => b.addEventListener("click", () => this._openDocFolder(b)))
    this.docFilesTarget.innerHTML = `<p class="px-4 py-2 text-[13px] text-gray-500">Pick a folder on the left.</p>`
  }

  async _openDocFolder(btn) {
    this.docFoldersTarget.querySelectorAll("[data-folder-id]").forEach(b => b.classList.toggle("bg-brand/10", b === btn))
    const id = btn.dataset.folderId
    this.docFilesTarget.innerHTML = `<p class="px-4 py-2 text-[13px] text-gray-500">Loading files…</p>`
    try {
      const url = `${this.documentsFilesUrlValue}?folder_id=${encodeURIComponent(id)}`
      const res = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!res.ok) throw new Error("files")
      const files = await res.json()
      if (!Array.isArray(files) || !files.length) {
        this.docFilesTarget.innerHTML = `<p class="px-4 py-2 text-[13px] text-gray-500">No files in this folder.</p>`
        return
      }
      this.docFilesTarget.innerHTML = files.map(f => `
        <label class="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer">
          <input type="checkbox" data-doc-id="${f.id}" data-doc-name="${this._escHtml(f.filename)}" ${this._docPicked.has(String(f.id)) ? "checked" : ""}
                 class="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand">
          <span class="flex-1 min-w-0">
            <span class="block text-[13px] text-gray-800 truncate">${this._escHtml(f.filename)}</span>
            <span class="block text-[12px] text-gray-400">${this._escHtml([f.size, f.formatted_date, f.uploader_name].filter(Boolean).join(" · "))}</span>
          </span>
        </label>`).join("")
      this.docFilesTarget.querySelectorAll("[data-doc-id]").forEach(cb => cb.addEventListener("change", () => {
        if (cb.checked) this._docPicked.set(cb.dataset.docId, cb.dataset.docName)
        else this._docPicked.delete(cb.dataset.docId)
        this._paintDocCount()
      }))
    } catch (_) {
      this.docFilesTarget.innerHTML = `<p class="px-4 py-2 text-[13px] text-red-600">Couldn't load this folder — try again.</p>`
    }
  }

  _paintDocCount() {
    const n = this._docPicked ? this._docPicked.size : 0
    if (this.hasDocAddBtnTarget) {
      this.docAddBtnTarget.disabled = n === 0
      this.docAddBtnTarget.textContent = n ? `Add ${n} file${n === 1 ? "" : "s"}` : "Add files"
    }
    if (this.hasDocCountTarget) {
      this.docCountTarget.textContent = n ? `${n} picked — they're added as copies.` : "Tick the files to add. They're added as copies."
    }
  }

  async addPickedDocs() {
    if (!this._docPicked || !this._docPicked.size || !this.hasFileListTarget) return
    const picked = [...this._docPicked.entries()]
    this._docPicked = new Map()
    this.closeDocPicker()
    this._startUpload()
    const placeholders = picked.map(([, name]) => {
      const chip = this._createFileChip(name, { uploading: true })
      this.fileListTarget.appendChild(chip)
      return chip
    })
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.attachDocumentsUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attachment_ids: picked.map(([id]) => id) })
      })
      if (!res.ok) throw new Error("attach")
      const data = await res.json()
      placeholders.forEach(p => p.remove())
      ;(data.files || []).forEach(a => this._addRegisteredFileChip(a))
      if (data.skipped) console.warn(`[action-item-form] ${data.skipped} picked document(s) could not be added`)
    } catch (e) {
      console.error("Pick from Documents failed:", e)
      placeholders.forEach(p => p.remove())
    } finally {
      this._finishUpload()
      this._paintDocCount()
    }
  }

  // A registered file on the item, as a removable chip (uploads and picked documents alike).
  _addRegisteredFileChip(a) {
    const name = a.filename || `File #${a.id}`
    this._fileIds.push(a.photo_id)
    this._fileNames[a.photo_id] = name
    if (this._queueDraftSave) this._queueDraftSave()
    const chip = this._createFileChip(name, {
      removable: true,
      onRemove: () => {
        chip.remove()
        this._fileIds = this._fileIds.filter(id => id !== a.photo_id)
      }
    })
    this.fileListTarget.appendChild(chip)
  }

  _escHtml(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML
  }

  // ── Upload state ───────────────────────────────────────────────────

  _startUpload() {
    this._pendingUploads++
    this._updateUploadUI()
  }

  _finishUpload() {
    this._pendingUploads--
    this._updateUploadUI()
  }

  _updateUploadUI() {
    const uploading = this._pendingUploads > 0
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = uploading
    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.classList.toggle("hidden", !uploading)
    }
    if (this.hasUploadStatusTextTarget && uploading) {
      const total = this._uploadPhotos.filter(Boolean).length + this._uploadFiles.filter(Boolean).length
      this.uploadStatusTextTarget.textContent = `Uploading ${total} file${total !== 1 ? "s" : ""}…`
    }
  }

  // ── Existing attachments (edit mode) ───────────────────────────────

  _loadExistingAttachments() {
    if (this.existingPhotosValue.length) {
      this.existingPhotosValue.forEach(photo => {
        const entry = { file: null, comment: photo.comment || "", convertedBlob: null, _filename: photo.filename || "", _uploadResult: { photo_id: photo.id, _existing: true, thumbnail_url: photo.thumbnail_url || null } }
        this._uploadPhotos.push(entry)
        const index = this._uploadPhotos.length - 1
        this._photoIds.push(photo.id)
        if (photo.comment) this._photoComments[photo.id] = photo.comment
        this._renderPhotoUploadRow(index, null, {
          filename: photo.filename,
          thumbnailUrl: photo.thumbnail_url,
          comment: photo.comment
        })
      })
    }

    if (this.existingFilesValue.length) {
      this.existingFilesValue.forEach(file => {
        this._fileIds.push(file.id)
        this._fileNames[file.id] = file.filename || `File #${file.id}`
        const chip = this._createFileChip(file.filename, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._fileIds = this._fileIds.filter(id => id !== file.id)
            this._removedIds.push(file.id)
          }
        })
        if (this.hasFileListTarget) this.fileListTarget.appendChild(chip)
      })
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────

  // "Save draft" (Larry 2026-09-08): only a title is needed; nobody is
  // notified and the item shows as a draft for you in the list.
  saveDraft() {
    this._submitMode = "draft"
    return this.submit()
  }

  _customFields() {
    const out = {}
    this.element.querySelectorAll('[name^="custom_fields["]').forEach((el) => {
      const key = (el.name.match(/^custom_fields\[(.+)\]$/) || [])[1]
      if (!key) return
      if (el.type === "checkbox") { if (el.checked) out[key] = "1"; else if (out[key] === undefined) out[key] = "" }
      else if (el.type === "hidden") { if (out[key] === undefined) out[key] = el.value }
      else out[key] = el.value
    })
    return out
  }

  async submit() {
    const draftSave = this._submitMode === "draft"
    this._submitMode = null
    this._clearAllErrors()

    // Wait for any in-flight uploads to finish
    if (this._eagerUploadPromises.length) {
      await Promise.allSettled(this._eagerUploadPromises)
    }

    const fields = draftSave ? [this.titleInputTarget] : [...this.element.querySelectorAll("[data-required]")]
    let valid = true

    fields.forEach(field => {
      if (!field.value.trim()) {
        this._showFieldError(field)
        valid = false
      }
    })

    // Read approver emails from the member-multi-select controller
    const pickerCtrl = this.application.getControllerForElementAndIdentifier(
      this.approversPickerTarget, "member-multi-select"
    )
    const approverEmails = pickerCtrl ? pickerCtrl.selectedEmails : this._approverEmails
    const approverList = approverEmails.join(" ")

    if (!approverList && !this.assignedToOptionalValue && !draftSave) {
      this.approversErrorTarget.classList.remove("hidden")
      valid = false
    }

    // Read distribution emails
    const distCtrl = this.hasDistributionPickerTarget
      ? this.application.getControllerForElementAndIdentifier(
          this.distributionPickerTarget, "member-multi-select"
        )
      : null
    const distributionEmails = distCtrl ? distCtrl.selectedEmails : this._distributionEmails

    if (!valid) {
      const firstError = this.element.querySelector(".border-red-400, [data-error-msg]:not(.hidden)")
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    // Read external user emails
    const extCtrl = this.hasExternalUsersPickerTarget
      ? this.application.getControllerForElementAndIdentifier(
          this.externalUsersPickerTarget, "external-users-select"
        )
      : null
    const externalEmails = extCtrl ? extCtrl.selectedEmails : this._externalUserEmails

    // Build photo comments map
    const photoComments = {}
    for (const [pid, comment] of Object.entries(this._photoComments)) {
      if (comment) photoComments[pid] = comment
    }

    const body = {
      punchlist: {
        title: this.titleInputTarget.value.trim(),
        item: this.descriptionInputTarget.value.trim(),
        target_date: this.targetDateInputTarget.value.trim(),
        approver_list: approverList,
        distribution_list: distributionEmails.join(" "),
        limited_users_list: externalEmails.join(" "),
        critical_level: this.criticalSelectTarget.value,
        punchlist_kind_id: this.kindSelectTarget.value || null,
        sequential: this.sequentialInputTarget.value || null,
        spec_drawings: this.hasSpecDrawingsInputTarget ? this.specDrawingsInputTarget.value : null,
        // Multi-activity: the hidden field holds a comma list; task_id stays
        // the first pick for single-activity readers.
        task_ids: this.hasTaskSelectTarget ? (this.taskSelectTarget.value || null) : null,
        task_id: this.hasTaskSelectTarget ? (this.taskSelectTarget.value.split(",")[0]?.trim() || null) : null
      },
      punchlist_kind_id: this.kindSelectTarget.value || null,
      // The company's own fields (Studio step 3): key → value, an unticked box as "".
      custom_fields: this._customFields(),
      photo_ids: this._photoIds,
      file_ids: this._fileIds,
      photo_comments: photoComments,
      removed_photo_ids: this._removedIds
    }

    // Checklist inside the item (K3-38): the switch rides in `punchlist`,
    // the lines as a sibling the server applies after the save.
    const checklist = this._checklistPayload()
    if (checklist) {
      body.punchlist.checklist_enabled = checklist.enabled
      body.punchlist.checklist_autocomplete = checklist.autocomplete
      body.checklist = checklist
    }

    // Submittal package + schedule (K3-40), only while the type is a submittal.
    if (this.hasSubmittalBoxTarget && !this.submittalBoxTarget.classList.contains("hidden")) {
      body.punchlist.needed_on_site = this.neededOnSiteInputTarget.value || ""
      body.punchlist.lead_time_days = this.leadTimeInputTarget.value || ""
      body.punchlist.review_days = this.reviewDaysInputTarget.value || ""
      const pkg = this.packageSelectTarget.value
      if (pkg === "__new__") body.submittal_package_name = this.packageNewInputTarget.value.trim()
      else body.punchlist.submittal_package_id = pkg || ""
    }

    // SLC3-11 (Phelipe 2026-09-29): the item's own email reminders ("" = off).
    // Locked (disabled) when the project's company sets them for every item.
    if (this.hasRemindBeforeSelectTarget && !this.remindBeforeSelectTarget.disabled) {
      body.punchlist.remind_before_days = this.remindBeforeSelectTarget.value
      body.punchlist.remind_after_days = this.hasRemindAfterSelectTarget ? this.remindAfterSelectTarget.value : ""
    }

    // Ball in Court at creation (Kloepfer item 13, builder only): sibling key
    // (not nested in `punchlist`) — the server resolves the member and writes
    // the ball_in_court JSON itself.
    const bicCtrl = this.hasBicPickerTarget
      ? this.application.getControllerForElementAndIdentifier(this.bicPickerTarget, "member-multi-select")
      : null
    const bicEmails = bicCtrl ? bicCtrl.selectedEmails : []
    if (bicEmails.length) {
      // Every pick when the company allows multiple Ball in Court (Phelipe
      // 2026-09-01); the first one also rides the older single key.
      body.ball_in_court_emails = bicEmails
      body.ball_in_court_email = bicEmails[0]
    }

    // § 3.5 Tier 4 — when the composer was opened from the BIM viewer
    // the form embeds hidden inputs carrying the 3D-context payload.
    // The controller reads them via `params[:bim_pin]` on create and
    // writes a PunchlistBimPin row alongside the punchlist. Submitted
    // as a sibling key (not nested in `punchlist`) so Strong Params
    // for AR `update` doesn't see them.
    const bimPinPayload = this._collectBimPinPayload()
    if (bimPinPayload) body.bim_pin = bimPinPayload

    if (draftSave) body.save_draft = "1"
    else if (this.draftValue) body.publish = "1"

    this.submitBtnTarget.disabled = true
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.submitUrlValue, {
        method: this.methodValue,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        body: JSON.stringify(body)
      })

      if (res.ok) {
        const toastMsg = draftSave ? "Draft saved — it's in your list marked Draft"
          : (this.isEditValue && !this.draftValue) ? "Action item updated" : "Action item created"
        sessionStorage.setItem("actionItemToast", JSON.stringify({ message: toastMsg, type: "notice" }))
        window.location.href = this.submitUrlValue.replace(/\/\d+$/, "").replace(/\.json$/, "")
      } else {
        const data = await res.json()
        const msgs = data.errors || ["An error occurred"]
        alert(msgs.join("\n"))
      }
    } catch (e) {
      alert("Network error. Please try again.")
      console.error("[ActionItemForm] submit error:", e)
    } finally {
      this.submitBtnTarget.disabled = false
        if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
    }
  }

  // ── HEIC handling ───────────────────────────────────────────────────

  _isHeic(file) {
    if (!file) return false
    const type = (file.type || "").toLowerCase()
    if (type.includes("heic") || type.includes("heif")) return true
    const ext = file.name?.split(".")?.pop()?.toLowerCase()
    return ext === "heic" || ext === "heif"
  }

  async _convertHeic(file, index, entry) {
    const row = this.photoListTarget?.querySelector(`[data-upload-photo-index="${index}"]`)
    const thumbDiv = row?.querySelector("[data-role='upload-thumb']")

    try {
      if (!this._heicWorker) {
        const workerUrl = document.querySelector('meta[name="heic-worker-url"]')?.content
        if (!workerUrl) { throw new Error("No HEIC worker URL") }
        this._heicWorker = new Worker(workerUrl)
        this._heicCallbacks = {}
        this._heicWorker.onmessage = (e) => {
          const cb = this._heicCallbacks[e.data.id]
          if (cb) {
            delete this._heicCallbacks[e.data.id]
            if (e.data.error) cb.reject(new Error(e.data.error))
            else cb.resolve(e.data)
          }
        }
        this._heicWorker.onerror = (e) => {
          console.error("HEIC worker error:", e)
          for (const [cbId, cb] of Object.entries(this._heicCallbacks)) {
            cb.reject(new Error("Worker error"))
            delete this._heicCallbacks[cbId]
          }
        }
      }

      const buffer = await file.arrayBuffer()
      const id = `heic_${Date.now()}_${index}`

      const result = await new Promise((resolve, reject) => {
        this._heicCallbacks[id] = { resolve, reject }
        this._heicWorker.postMessage({ id, buffer }, [buffer])
      })

      const canvas = document.createElement("canvas")
      canvas.width = result.width
      canvas.height = result.height
      const ctx = canvas.getContext("2d")
      const imageData = new ImageData(
        new Uint8ClampedArray(result.data),
        result.width,
        result.height
      )
      ctx.putImageData(imageData, 0, 0)

      const converted = await new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), "image/jpeg", 0.8)
      })

      entry.convertedBlob = converted

      if (thumbDiv) {
        const blobUrl = URL.createObjectURL(converted)
        thumbDiv.innerHTML = `<img src="${blobUrl}" class="w-full h-full object-cover">`
      }
    } catch (e) {
      console.warn("HEIC conversion failed, server will handle it:", e)
      if (thumbDiv) {
        thumbDiv.innerHTML = this._imagePlaceholderSvg()
      }
    }
  }

  // ── Compression ─────────────────────────────────────────────────────

  async _prepareFile(entry) {
    if (entry._heicPromise) await entry._heicPromise
    const file = entry.file
    const source = entry.convertedBlob || file
    if (!this._isCompressibleImage(source)) {
      return { name: file.name, blob: source }
    }
    const blob = await this._compressImage(source)
    return { name: file.name, blob }
  }

  _isCompressibleImage(file) {
    return /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)
  }

  _compressImage(file, maxDim = 2000, quality = 0.80) {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        let { width, height } = img

        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => resolve(blob || file),
          "image/jpeg",
          quality
        )
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        resolve(file)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  // ── Thumbnail polling ───────────────────────────────────────────────

  _pollForThumbnails(photoIds) {
    if (!this.pollThumbnailsUrlValue || !photoIds.length) return

    let pending = new Set(photoIds)
    let attempts = 0
    const maxAttempts = 25

    const poll = async () => {
      if (pending.size === 0 || attempts >= maxAttempts) return
      attempts++

      try {
        const url = new URL(this.pollThumbnailsUrlValue, window.location.origin)
        pending.forEach(id => url.searchParams.append("ids[]", id))

        const res = await fetch(url, { headers: { "Accept": "application/json" } })
        if (!res.ok) {
          if (pending.size > 0) setTimeout(poll, 3000)
          return
        }

        const data = await res.json()
        const photos = data.photos || {}

        for (const [idStr, info] of Object.entries(photos)) {
          if (!info.ready) continue
          const id = parseInt(idStr)

          const entryIdx = this._uploadPhotos.findIndex(
            e => e?._uploadResult?.photo_id === id
          )
          if (entryIdx >= 0 && info.thumbnail_url) {
            this._uploadPhotos[entryIdx]._uploadResult.thumbnail_url = info.thumbnail_url
            if (this._queueDraftSave) this._queueDraftSave()
            const row = this.photoListTarget?.querySelector(
              `[data-upload-photo-index="${entryIdx}"]`
            )
            const thumbDiv = row?.querySelector("[data-role='upload-thumb']")
            if (thumbDiv) {
              const img = new Image()
              img.src = info.thumbnail_url
              img.onload = () => {
                thumbDiv.innerHTML = `<img src="${info.thumbnail_url}" class="w-full h-full object-cover">`
              }
            }
          }

          pending.delete(id)
        }
      } catch (e) {
        console.error("Thumbnail poll failed:", e)
      }

      if (pending.size > 0) {
        setTimeout(poll, attempts <= 3 ? 2000 : 3000)
      }
    }

    setTimeout(poll, 2000)
  }

  // ── File chip ──────────────────────────────────────────────────────

  _createFileChip(name, { removable = false, uploading = false, onRemove = null } = {}) {
    const chip = document.createElement("div")
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${name}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // ── SVG helpers ─────────────────────────────────────────────────────

  _spinnerSvg() {
    return `<svg class="w-5 h-5 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _imagePlaceholderSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/></svg>`
  }

  _xSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`
  }

  // ── Field-level validation ──────────────────────────────────────────

  _showFieldError(field, message) {
    field.classList.add("border-red-400", "ring-1", "ring-red-400")
    const msg = field.parentElement.querySelector("[data-error-msg]") ||
                field.closest("div").querySelector("[data-error-msg]")
    if (msg) {
      if (message) msg.textContent = message
      msg.classList.remove("hidden")
    }
    field.addEventListener("input", () => this._clearFieldError(field), { once: true })
  }

  _clearFieldError(field) {
    field.classList.remove("border-red-400", "ring-1", "ring-red-400")
    const msg = field.parentElement.querySelector("[data-error-msg]") ||
                field.closest("div").querySelector("[data-error-msg]")
    if (msg) msg.classList.add("hidden")
  }

  _clearAllErrors() {
    this.element.querySelectorAll("[data-error-msg]").forEach(el => el.classList.add("hidden"))
    this.element.querySelectorAll(".border-red-400").forEach(el => {
      el.classList.remove("border-red-400", "ring-1", "ring-red-400")
    })
  }

  // § 3.5 Tier 4 — gather hidden inputs the form template renders when
  // the composer was opened from the BIM viewer. Returns null when no
  // 3D context is attached so the post body skips the `bim_pin` key
  // entirely (the controller's Strong-Params equivalent only writes
  // the row when `bim_model_id` is present).
  _collectBimPinPayload() {
    const fields = this.element.querySelectorAll("[data-bim-pin-field]")
    if (fields.length === 0) return null
    const payload = { element_external_ids: [], position: {} }
    fields.forEach(el => {
      const key = el.dataset.bimPinField
      const val = el.value
      if (val == null || val === "") return
      if (key === "element_external_ids") {
        payload.element_external_ids.push(val)
      } else if (key === "pos_x" || key === "pos_y" || key === "pos_z") {
        payload.position[key.slice(-1)] = Number(val)
      } else {
        payload[key] = val
      }
    })
    if (!payload.bim_model_id) return null
    return payload
  }

  // ── Checklist inside the item (Keane K3-38, Phelipe 2026-10-13) ──────────
  // One switch, off by default. On, the lines typed here are saved with the
  // item (`checklist` in the submit body) and can be ticked off inside it.

  _initChecklist() {
    if (!this.hasChecklistSectionTarget) return
    const el = this.checklistSectionTarget
    const parse = (s, fallback) => { try { return JSON.parse(s) } catch (_) { return fallback } }
    this._checklist = {
      enabled: el.dataset.enabled === "true",
      autocomplete: el.dataset.autocomplete !== "false",
      lines: parse(el.dataset.lines, []).map(l => ({ id: l.id || null, text: l.text || "", assignee_user_id: l.assignee_user_id || null, done: !!l.done, depth: l.depth === 1 ? 1 : 0, source: "manual" }))
    }
    this._normalizeChecklistDepths()
    this._checklistMembers = parse(el.dataset.members, [])
    this._checklistTemplates = null
    this._checklistRecognition = null
    this._renderChecklistLines()
    this._applyChecklistLayout()
    this._onChecklistResize = () => this._applyChecklistLayout()
    window.addEventListener("resize", this._onChecklistResize)
  }

  // The page splits in two once the checklist is on (Phelipe 2026-10-13):
  // the fields keep the left column, the checklist box moves into the aside
  // on the right and the page frame widens. Narrow screens keep the box
  // under its switch.
  _applyChecklistLayout() {
    if (!this._checklist || !this.hasChecklistColumnTarget || !this.hasChecklistBoxTarget) return
    const wide = window.matchMedia("(min-width: 1024px)").matches
    const split = this._checklist.enabled && wide
    const box = this.checklistBoxTarget
    const aside = this.checklistColumnTarget
    const frame = this.element.closest("[data-action-item-form-frame]")
    if (split) {
      if (box.parentElement !== aside) aside.appendChild(box)
      aside.classList.remove("hidden")
      this.layoutTarget.classList.add("lg:grid-cols-2")
      if (frame) { frame.classList.remove("max-w-3xl"); frame.classList.add("max-w-6xl") }
      box.classList.remove("mt-2")
    } else {
      if (box.parentElement === aside && this.hasChecklistSwitchRowTarget) this.checklistSwitchRowTarget.insertAdjacentElement("afterend", box)
      aside.classList.add("hidden")
      this.layoutTarget.classList.remove("lg:grid-cols-2")
      if (frame) { frame.classList.remove("max-w-6xl"); frame.classList.add("max-w-3xl") }
      box.classList.add("mt-2")
    }
  }

  toggleChecklist() {
    if (!this._checklist) return
    this._checklist.enabled = !this._checklist.enabled
    const on = this._checklist.enabled
    this._setChecklistSwitch(this.checklistSwitchTarget, this.checklistKnobTarget, on)
    this.checklistBoxTarget.classList.toggle("hidden", !on)
    if (this.hasChecklistSwitchRowTarget) {
      this.checklistSwitchRowTarget.classList.toggle("border-amber-300", on)
      this.checklistSwitchRowTarget.classList.toggle("bg-amber-50", on)
      this.checklistSwitchRowTarget.classList.toggle("border-gray-200", !on)
      this.checklistSwitchRowTarget.classList.toggle("bg-white", !on)
    }
    if (this.hasChecklistStateTarget) {
      this.checklistStateTarget.textContent = on
        ? "On. Lines you add here can be ticked off inside the item."
        : "Off. Turn it on to tick things off inside this item."
    }
    this._applyChecklistLayout()
    if (on && this.hasChecklistNewInputTarget) this.checklistNewInputTarget.focus()
  }

  toggleChecklistAutocomplete() {
    if (!this._checklist) return
    this._checklist.autocomplete = !this._checklist.autocomplete
    this._setChecklistSwitch(this.checklistAutoSwitchTarget, this.checklistAutoKnobTarget, this._checklist.autocomplete)
  }

  _setChecklistSwitch(btn, knob, on) {
    btn.setAttribute("aria-checked", on ? "true" : "false")
    btn.classList.toggle("bg-brand", on)
    btn.classList.toggle("bg-gray-300", !on)
    knob.classList.toggle("translate-x-4", on)
  }

  // A sub-line needs a line above it; the first line is always top-level.
  _normalizeChecklistDepths() {
    let seenTop = false
    this._checklist.lines.forEach(l => {
      if (l.depth === 1 && !seenTop) l.depth = 0
      if (l.depth !== 1) seenTop = true
    })
  }

  _renderChecklistLines() {
    if (!this.hasChecklistListTarget) return
    this._normalizeChecklistDepths()
    const members = this._checklistMembers || []
    let top = 0, sub = 0
    const rows = this._checklist.lines.map((line, i) => {
      const isSub = line.depth === 1
      if (isSub) sub += 1; else { top += 1; sub = 0 }
      const number = isSub ? `${top}.${sub}` : `${top}.`
      const canIndent = !isSub && i > 0
      const options = [`<option value="">${this._escHtml("— person —")}</option>`]
        .concat(members.map(m => `<option value="${m.id}" ${String(m.id) === String(line.assignee_user_id || "") ? "selected" : ""}>${this._escHtml(m.name || m.email || "")}</option>`))
        .join("")
      const box = line.done
        ? `<span class="w-4 h-4 rounded bg-emerald-500 text-white text-[10px] leading-4 text-center shrink-0" title="Ticked">✓</span>`
        : `<span class="w-4 h-4 rounded border border-gray-300 bg-gray-50 shrink-0"></span>`
      const depthBtn = isSub
        ? `<button type="button" data-index="${i}" data-action="click->action-item-form#outdentChecklistLine" class="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer" title="Move out (Shift+Tab)"><svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"/></svg></button>`
        : canIndent
          ? `<button type="button" data-index="${i}" data-action="click->action-item-form#indentChecklistLine" class="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer" title="Make it a sub-line of the line above (Tab)"><svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m15 15 6-6m0 0-6-6m6 6H9a6 6 0 0 0 0 12h3"/></svg></button>`
          : ""
      return `<div class="flex items-center gap-2 py-1.5 pr-3 border-b border-gray-100 last:border-b-0 ${isSub ? "pl-8 bg-gray-50/60" : "pl-1"}" data-checklist-line="${i}"
                  data-action="dragover->action-item-form#checklistDragOver drop->action-item-form#checklistDrop">
        <span draggable="true" data-index="${i}" data-action="dragstart->action-item-form#checklistDragStart dragend->action-item-form#checklistDragEnd"
              class="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 select-none shrink-0 px-0.5" title="Drag to reorder">
          <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><circle cx="7" cy="5" r="1.5"/><circle cx="13" cy="5" r="1.5"/><circle cx="7" cy="10" r="1.5"/><circle cx="13" cy="10" r="1.5"/><circle cx="7" cy="15" r="1.5"/><circle cx="13" cy="15" r="1.5"/></svg>
        </span>
        <span class="text-gray-300 select-none text-xs w-6 text-right" title="Line ${number}">${number}</span>
        ${box}
        <input type="text" value="${this._escHtml(line.text)}" data-index="${i}"
               data-action="input->action-item-form#checklistLineTextChanged keydown->action-item-form#checklistLineKeydown"
               class="flex-1 min-w-0 px-1 py-0.5 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 ${line.done ? "text-gray-400 line-through" : "text-gray-900"}">
        <select data-index="${i}" data-action="change->action-item-form#checklistLineAssigneeChanged"
                class="text-[13px] text-gray-600 bg-transparent border-0 focus:outline-none cursor-pointer max-w-[170px] truncate" title="Who does this line">${options}</select>
        ${depthBtn}
        <button type="button" data-index="${i}" data-action="click->action-item-form#removeChecklistLine"
                class="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer" title="Remove">${this._xSvg()}</button>
      </div>`
    })
    this.checklistListTarget.innerHTML = rows.join("")
    if (this.hasChecklistCountTarget) {
      const n = this._checklist.lines.filter((l, i, arr) => !(l.depth !== 1 && arr[i + 1] && arr[i + 1].depth === 1)).length
      this.checklistCountTarget.textContent = n === 1 ? "1 line to tick" : `${n} lines to tick`
    }
  }

  // Tab = sub-line of the line above, Shift+Tab = back out, Enter = a new
  // line right under this one at the same level.
  indentChecklistLine(e) {
    const i = Number(e.currentTarget.dataset.index)
    this._setChecklistDepth(i, 1)
  }

  outdentChecklistLine(e) {
    const i = Number(e.currentTarget.dataset.index)
    this._setChecklistDepth(i, 0)
  }

  _setChecklistDepth(i, depth) {
    const line = this._checklist.lines[i]
    if (!line || i === 0 && depth === 1) return
    line.depth = depth
    if (depth === 0) {
      // The sub-lines that followed it now follow it as its own sub-lines — fine as they are.
    } else {
      // Its own sub-lines come back out one level, as lines of their own.
      for (let j = i + 1; j < this._checklist.lines.length && this._checklist.lines[j].depth === 1; j++) {
        // they stay depth 1 but now belong to the line above i — nothing to change
      }
    }
    this._renderChecklistLines()
    const input = this.checklistListTarget.querySelector(`[data-checklist-line="${i}"] input[type="text"]`)
    if (input) input.focus()
  }

  // Drag a line (a parent brings its sub-lines) to a new place in the list.
  checklistDragStart(e) {
    this._dragIndex = Number(e.currentTarget.dataset.index)
    e.dataTransfer.effectAllowed = "move"
    try { e.dataTransfer.setData("text/plain", String(this._dragIndex)) } catch (_) { /* some browsers refuse */ }
    e.currentTarget.closest("[data-checklist-line]")?.classList.add("opacity-50")
  }

  checklistDragEnd(e) {
    e.currentTarget.closest("[data-checklist-line]")?.classList.remove("opacity-50")
    this._clearChecklistDropMarks()
  }

  checklistDragOver(e) {
    if (this._dragIndex === undefined || this._dragIndex === null) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    const row = e.currentTarget
    this._clearChecklistDropMarks()
    if (Number(row.dataset.checklistLine) === this._dragIndex) return
    const rect = row.getBoundingClientRect()
    const below = e.clientY > rect.top + rect.height / 2
    row.classList.add(below ? "border-b-2" : "border-t-2", "border-brand")
    row.dataset.dropBelow = below ? "1" : "0"
  }

  _clearChecklistDropMarks() {
    if (!this.hasChecklistListTarget) return
    this.checklistListTarget.querySelectorAll("[data-checklist-line]").forEach(r => {
      r.classList.remove("border-b-2", "border-t-2", "border-brand")
      delete r.dataset.dropBelow
    })
  }

  checklistDrop(e) {
    if (this._dragIndex === undefined || this._dragIndex === null) return
    e.preventDefault()
    const from = this._dragIndex
    this._dragIndex = null
    const row = e.currentTarget
    const below = row.dataset.dropBelow === "1"
    this._clearChecklistDropMarks()
    const targetIndex = Number(row.dataset.checklistLine)
    if (targetIndex === from) return
    const lines = this._checklist.lines
    const dragged = lines[from]
    const blockLen = dragged.depth !== 1 ? 1 + lines.slice(from + 1).findIndex(l => l.depth !== 1) : 1
    const block = lines.slice(from, from + (blockLen > 0 ? blockLen : lines.length - from))
    const rest = lines.filter(l => !block.includes(l))
    const target = lines[targetIndex]
    let at = rest.indexOf(target)
    if (at < 0) return
    if (below) {
      let j = at
      if (target.depth !== 1) while (rest[j + 1] && rest[j + 1].depth === 1) j++
      at = j + 1
    }
    this._checklist.lines = rest.slice(0, at).concat(block, rest.slice(at))
    this._renderChecklistLines()
  }

  _insertChecklistLineAfter(i) {
    const current = this._checklist.lines[i]
    const line = { id: null, text: "", assignee_user_id: null, done: false, depth: current ? current.depth : 0, source: "manual" }
    this._checklist.lines.splice(i + 1, 0, line)
    this._renderChecklistLines()
    const input = this.checklistListTarget.querySelector(`[data-checklist-line="${i + 1}"] input[type="text"]`)
    if (input) input.focus()
  }

  checklistInputKeydown(e) {
    if (e.key !== "Enter") return
    e.preventDefault()
    this.addChecklistLineFromInput()
  }

  addChecklistLineFromInput() {
    if (!this.hasChecklistNewInputTarget) return
    const text = this.checklistNewInputTarget.value.trim()
    if (!text) return
    this._addChecklistLines([{ text }], "manual")
    this.checklistNewInputTarget.value = ""
    this.checklistNewInputTarget.focus()
  }

  // lines: [{text, assignee_user_id?}] — a name after a dash is resolved by
  // the server for pasted/dictated text; here we only keep what's given.
  _addChecklistLines(lines, source) {
    const added = lines.map(l => ({ id: null, text: (l.text || "").toString().trim().slice(0, 500), assignee_user_id: l.assignee_user_id || null, done: false, depth: l.depth === 1 ? 1 : 0, source }))
      .filter(l => l.text)
    if (!added.length) return 0
    this._checklist.lines = this._checklist.lines.concat(added).slice(0, 200)
    if (!this._checklist.enabled) this.toggleChecklist()
    this._renderChecklistLines()
    return added.length
  }

  checklistLineTextChanged(e) {
    const i = Number(e.currentTarget.dataset.index)
    if (this._checklist.lines[i]) this._checklist.lines[i].text = e.currentTarget.value
  }

  checklistLineKeydown(e) {
    const i = Number(e.currentTarget.dataset.index)
    if (e.key === "Enter") { e.preventDefault(); this._insertChecklistLineAfter(i); return }
    if (e.key === "Tab") {
      e.preventDefault()
      this._setChecklistDepth(i, e.shiftKey ? 0 : 1)
      return
    }
    // Backspace on an empty line removes it and goes back to the one above.
    if (e.key === "Backspace" && !e.currentTarget.value && this._checklist.lines.length > 1) {
      e.preventDefault()
      this._checklist.lines.splice(i, 1)
      this._renderChecklistLines()
      const prev = this.checklistListTarget.querySelector(`[data-checklist-line="${Math.max(0, i - 1)}"] input[type="text"]`)
      if (prev) { prev.focus(); prev.setSelectionRange(prev.value.length, prev.value.length) }
    }
  }

  checklistLineAssigneeChanged(e) {
    const i = Number(e.currentTarget.dataset.index)
    if (this._checklist.lines[i]) this._checklist.lines[i].assignee_user_id = e.currentTarget.value ? Number(e.currentTarget.value) : null
  }

  removeChecklistLine(e) {
    const i = Number(e.currentTarget.dataset.index)
    this._checklist.lines.splice(i, 1)
    this._renderChecklistLines()
  }

  // Paste a list
  openChecklistPaste() {
    this.checklistPasteBoxTarget.classList.remove("hidden")
    this.checklistPasteInputTarget.focus()
  }

  closeChecklistPaste() {
    this.checklistPasteBoxTarget.classList.add("hidden")
  }

  async addChecklistPaste() {
    const text = this.checklistPasteInputTarget.value
    if (!text.trim()) { this.closeChecklistPaste(); return }
    const lines = await this._checklistParseText(text)
    const n = this._addChecklistLines(lines, "paste")
    this.checklistPasteInputTarget.value = ""
    this.closeChecklistPaste()
    this._checklistToast(n === 1 ? "Added 1 line" : `Added ${n} lines`)
  }

  // The server splits the text and matches "– Name" to project members;
  // offline (or on an error) we split it ourselves.
  async _checklistParseText(text) {
    const url = this.checklistSectionTarget.dataset.parseUrl
    try {
      const res = await fetch(url, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ text })
      })
      if (res.ok) {
        const data = await res.json()
        if (Array.isArray(data.lines)) return data.lines
      }
    } catch (_) { /* fall through */ }
    const out = []
    text.split(/\r?\n/).forEach(raw => {
      if (!raw.trim()) return
      const indented = /^(?: {2,}|\t)/.test(raw) || /^\s*(?:\d{1,3}\.\d{1,3}[.)]?|[a-z][.)])\s+/i.test(raw)
      const t = raw.replace(/^\s*(?:\d{1,3}\.\d{1,3}[.)]?|[a-z][.)])\s+/i, "").replace(/^\s*(?:[-*•▪◦·]|\d{1,3}[.)])\s*/, "").trim()
      if (t) out.push({ text: t, depth: indented && out.some(x => x.depth === 0) ? 1 : 0 })
    })
    return out
  }

  // Dictate (the browser's speech recognition, where it exists)
  dictateChecklist() {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Rec) { this._checklistToast("Dictation isn't available in this browser — type or paste the lines instead.", "alert"); return }
    if (this._checklistRecognition) { this._stopChecklistDictation(); return }
    const rec = new Rec()
    rec.lang = document.documentElement.lang || "en-US"
    rec.continuous = true
    rec.interimResults = false
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (!ev.results[i].isFinal) continue
        const said = ev.results[i][0].transcript.trim()
        if (!said) continue
        // "new line" / "next" / "period" break one breath into several lines.
        const parts = said.split(/\b(?:new line|next item|next line|next|period)\b/i).map(s => s.trim()).filter(Boolean)
        this._addChecklistLines(parts.map(t => ({ text: t.charAt(0).toUpperCase() + t.slice(1) })), "dictate")
      }
    }
    rec.onerror = () => this._stopChecklistDictation()
    rec.onend = () => { if (this._checklistRecognition) this._stopChecklistDictation() }
    this._checklistRecognition = rec
    rec.start()
    if (this.hasChecklistDictateLabelTarget) this.checklistDictateLabelTarget.textContent = "Listening… tap to stop"
    if (this.hasChecklistDictateBtnTarget) this.checklistDictateBtnTarget.classList.add("text-rose-600")
  }

  _stopChecklistDictation() {
    const rec = this._checklistRecognition
    this._checklistRecognition = null
    try { rec && rec.stop() } catch (_) { /* already stopped */ }
    if (this.hasChecklistDictateLabelTarget) this.checklistDictateLabelTarget.textContent = "Dictate"
    if (this.hasChecklistDictateBtnTarget) this.checklistDictateBtnTarget.classList.remove("text-rose-600")
  }

  // From a photo of notes: the picture goes to S3 like any upload (presign →
  // PUT), then the server's AI reads the list in it.
  async checklistFromPhoto(e) {
    const file = e.currentTarget.files && e.currentTarget.files[0]
    e.currentTarget.value = ""
    if (file) this._checklistReadUpload(file, "photo")
  }

  // From a file (2026-10-15): Word, Excel, CSV, PDF, text or a picture —
  // the same upload, and the server's AI picks the list out of the file.
  async checklistFromFile(e) {
    const file = e.currentTarget.files && e.currentTarget.files[0]
    e.currentTarget.value = ""
    if (file) this._checklistReadUpload(file, "file")
  }

  async _checklistReadUpload(file, kind) {
    const fromFile = kind === "file"
    this._checklistToast(fromFile ? "Reading the list in your file…" : "Reading the list in your photo…")
    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      if (!presignRes.ok) throw new Error("presign")
      const { uploads } = await presignRes.json()
      const up = uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": file.type || up.content_type }, body: file })
      if (!put.ok) throw new Error("put")
      const res = await fetch(this.checklistSectionTarget.dataset.readPhotoUrl, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ cache_name: up.cache_name })
      })
      const data = await res.json().catch(() => ({}))
      const lines = Array.isArray(data.lines) ? data.lines : []
      if (!lines.length) { this._checklistToast(data.error || (fromFile ? "ConsLog AI couldn't find a list in that file." : "ConsLog AI couldn't read a list in that picture."), "alert"); return }
      const n = this._addChecklistLines(lines, kind)
      this._checklistToast(`Read ${n} ${n === 1 ? "line" : "lines"} from the ${fromFile ? "file" : "photo"} — check them.`)
    } catch (err) {
      console.error(`[ActionItemForm] checklist ${kind}:`, err)
      this._checklistToast(fromFile ? "Couldn't read that file. Try again." : "Couldn't read that photo. Try again.", "alert")
    }
  }

  // Templates
  async loadChecklistTemplates() {
    if (this._checklistTemplates) return
    try {
      const res = await fetch(this.checklistSectionTarget.dataset.templatesUrl, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      const data = await res.json()
      this._checklistTemplates = Array.isArray(data.templates) ? data.templates : []
    } catch (_) {
      this._checklistTemplates = []
    }
    const sel = this.checklistTemplateSelectTarget
    const current = sel.value
    sel.innerHTML = `<option value="">Start from a template</option>` +
      this._checklistTemplates.map(t => `<option value="${t.id}">${this._escHtml(t.name)} (${t.lines.length})</option>`).join("") +
      (this._checklistTemplates.length ? "" : `<option value="" disabled>No templates yet — Save as template makes one</option>`)
    sel.value = current
  }

  applyChecklistTemplate() {
    const sel = this.checklistTemplateSelectTarget
    const tpl = (this._checklistTemplates || []).find(t => String(t.id) === sel.value)
    sel.value = ""
    if (!tpl) return
    const n = this._addChecklistLines(tpl.lines.map(t => ({ text: t })), "template")
    this._checklistToast(`Started from "${tpl.name}" — ${n} ${n === 1 ? "line" : "lines"} added.`)
  }

  saveChecklistTemplate() {
    if (!this._checklist.lines.filter(l => l.text.trim()).length) { this._checklistToast("Add a few lines first.", "alert"); return }
    this.checklistTemplateNameBoxTarget.classList.remove("hidden")
    this.checklistTemplateNameInputTarget.value = this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : ""
    this.checklistTemplateNameInputTarget.focus()
  }

  cancelSaveChecklistTemplate() {
    this.checklistTemplateNameBoxTarget.classList.add("hidden")
  }

  async confirmSaveChecklistTemplate() {
    const name = this.checklistTemplateNameInputTarget.value.trim()
    if (!name) { this.checklistTemplateNameInputTarget.focus(); return }
    const lines = this._checklist.lines.map(l => l.text.trim()).filter(Boolean)
    try {
      const res = await fetch(this.checklistSectionTarget.dataset.templatesUrl, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ name, lines })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { this._checklistToast(data.error || "Couldn't save the template.", "alert"); return }
      this._checklistTemplates = null
      this.cancelSaveChecklistTemplate()
      this._checklistToast(`Saved "${name}" as a template for your company.`)
    } catch (_) {
      this._checklistToast("Couldn't save the template.", "alert")
    }
  }

  // AI: lines from the title, type, notes and the project's specs.
  async suggestChecklistLines() {
    const btn = this.hasChecklistAiBtnTarget ? this.checklistAiBtnTarget : null
    const title = this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : ""
    const notes = this.hasDescriptionInputTarget ? this.descriptionInputTarget.value.trim() : ""
    if (!title && !notes) { this._checklistToast("Give the item a title or a few notes first, so the AI knows what it's about.", "alert"); return }
    const kindSel = this.hasKindSelectTarget ? this.kindSelectTarget : null
    const kind = kindSel && kindSel.selectedIndex >= 0 ? (kindSel.options[kindSel.selectedIndex]?.text || "") : ""
    if (btn) btn.disabled = true
    try {
      const res = await fetch(this.checklistSectionTarget.dataset.suggestUrl, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ title, kind, notes, spec_drawings: this.hasSpecDrawingsInputTarget ? this.specDrawingsInputTarget.value : "" })
      })
      const data = await res.json().catch(() => ({}))
      const lines = Array.isArray(data.lines) ? data.lines : []
      if (!lines.length) { this._checklistToast(data.error || "ConsLog AI couldn't come up with lines for this.", "alert"); return }
      const n = this._addChecklistLines(lines, "ai")
      this._checklistToast(`AI suggested ${n} ${n === 1 ? "line" : "lines"} — remove any you don't want.`)
    } catch (_) {
      this._checklistToast("ConsLog AI couldn't run — try again in a moment.", "alert")
    } finally {
      if (btn) btn.disabled = false
    }
  }

  // What the submit sends. Lines ride along only while the switch is on;
  // switching it off keeps them on the server, just out of sight.
  _checklistPayload() {
    if (!this._checklist) return null
    const payload = { enabled: this._checklist.enabled, autocomplete: this._checklist.autocomplete }
    if (this._checklist.enabled) {
      this._normalizeChecklistDepths()
      payload.lines = this._checklist.lines
        .map(l => ({ id: l.id, text: l.text.trim(), assignee_user_id: l.assignee_user_id, depth: l.depth === 1 ? 1 : 0, source: l.source || "manual" }))
        .filter(l => l.text)
    }
    return payload
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  _checklistToast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = `flash-toast-${type === "alert" ? "alert" : "notice"}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    el.innerHTML = `<p class="flex-1 text-sm">${this._escHtml(message)}</p>`
    document.body.appendChild(el)
  }

  // ── Submittal package + schedule (K3-40) ────────────────────────────────
  // Shown while the picked type is a submittal; submit-by = needed on site
  // − lead time − review days, worked out as the person types.

  _toggleSubmittalBox() {
    if (!this.hasSubmittalBoxTarget || !this.hasKindSelectTarget) return
    const sel = this.kindSelectTarget
    const name = sel.selectedIndex >= 0 ? (sel.options[sel.selectedIndex]?.text || "") : ""
    const on = /submittal|submission|shop drawing/i.test(name)
    this.submittalBoxTarget.classList.toggle("hidden", !on)
    if (on) this.submitByChanged()
  }

  packageChanged() {
    const isNew = this.packageSelectTarget.value === "__new__"
    this.packageNewInputTarget.classList.toggle("hidden", !isNew)
    if (isNew) this.packageNewInputTarget.focus()
  }

  submitByChanged() {
    if (!this.hasSubmitByTextTarget) return
    const needed = this.neededOnSiteInputTarget.value
    if (!needed) { this.submitByTextTarget.textContent = "Pick when it's needed on site to work out the submit-by date."; return }
    const d = new Date(needed + "T00:00:00")
    if (isNaN(d)) { this.submitByTextTarget.textContent = ""; return }
    d.setDate(d.getDate() - Number(this.leadTimeInputTarget.value || 0) - Number(this.reviewDaysInputTarget.value || 0))
    const text = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    this.submitByTextTarget.textContent = `Submit by ${text} — needed on site minus lead time minus review.`
    if (this.hasTargetDateInputTarget && !this.targetDateInputTarget.value) {
      // The due date follows the math until the person sets one themselves.
      const fp = this.targetDateInputTarget._flatpickr
      const iso = d.toISOString().slice(0, 10)
      if (fp) fp.setDate(iso, false); else this.targetDateInputTarget.value = iso
    }
  }
}
